import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, Camera, Upload, Sparkles, CheckCircle2, AlertTriangle, 
  RotateCcw, Package, Loader2, ArrowRight, Layers, FileText, Check, ShieldCheck
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';
import { SoundManager } from '../../utils/SoundManager';

interface ScannedItem {
  status: 'created' | 'restocked' | 'updated';
  action: 'CREATED_NEW' | 'RESTOCKED_EXISTING' | 'UPDATED_EXISTING';
  productId?: number;
  name: string;
  scannedName: string;
  category: string;
  previousStock: number;
  restockQty: number;
  newStock: number;
  previousPrice?: number;
  sellingPrice: number;
  costPrice: number | null;
  previousExpiryDate?: string | null;
  expiryDate?: string | null;
  batchNumber?: string | null;
  unit: string;
  barcode: string | null;
  confidence: 'high' | 'medium' | 'low';
  priceUpdated?: boolean;
  expiryUpdated?: boolean;
}

interface ScanResultData {
  success: boolean;
  modelUsed: string;
  summary: string;
  totalExtracted: number;
  addedCount: number;
  restockedCount: number;
  items: ScannedItem[];
}

interface InventorySheetScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  onInventoryUpdated: () => void;
  showToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

export default function InventorySheetScannerModal({
  isOpen,
  onClose,
  storeApiKey,
  onInventoryUpdated,
  showToast
}: InventorySheetScannerModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResultData | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'created' | 'restocked'>('all');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  // Clean up states on close
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setSelectedFile(null);
      setPreviewUrl(null);
      setNotes('');
      setIsScanning(false);
      setScanResult(null);
      setErrorMsg('');
    }
  }, [isOpen]);

  const stopCamera = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    setCameraActive(false);
  };

  const startCamera = async () => {
    setErrorMsg('');
    try {
      setCameraActive(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      mediaStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err: any) {
      setCameraActive(false);
      setErrorMsg('Cannot access camera: ' + (err?.message || 'Permission denied'));
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 1280;
    canvas.height = videoRef.current.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
      setPreviewUrl(dataUrl);
      stopCamera();
      Haptics.fire('scan');
      SoundManager.play('product_select');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErrorMsg('');
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
    Haptics.fire('tap');
  };

  const executeScan = async () => {
    if (!previewUrl) {
      setErrorMsg('Please capture or select an inventory sheet image first.');
      return;
    }

    setIsScanning(true);
    setErrorMsg('');
    setScanResult(null);
    Haptics.fire('tap');

    try {
      const token = localStorage.getItem('kangavena:cashierToken') || '';
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-store-api-key': storeApiKey,
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch('/api/inventory/scan-image', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          imageBase64: previewUrl,
          additionalNotes: notes
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.detail || 'Failed to scan inventory sheet');
      }

      setScanResult(data);
      Haptics.fire('success');
      SoundManager.play('sale_complete');
      showToast(`✨ Successfully processed: ${data.addedCount} new, ${data.restockedCount} restocked!`, 'success');
      onInventoryUpdated();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to analyze inventory image');
      Haptics.fire('error');
    } finally {
      setIsScanning(false);
    }
  };

  if (!isOpen) return null;

  const filteredItems = scanResult?.items ? scanResult.items.filter(item => {
    if (activeTab === 'created') return item.status === 'created';
    if (activeTab === 'restocked') return item.status === 'restocked' || item.status === 'updated';
    return true;
  }) : [];

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-5 bg-black/80 backdrop-blur-xl animate-fade-in font-sans">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        className="relative w-full max-w-3xl max-h-[92vh] flex flex-col bg-[oklch(0.18_0.01_270)] border border-white/10 rounded-3xl shadow-[0_0_80px_rgba(0,0,0,0.8),inset_0_1px_0_0_rgba(255,255,255,0.1)] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-black/30 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center shadow-[0_0_20px_rgba(6,182,212,0.2)]">
              <Camera className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-white tracking-tight">Multimodal Inventory Vision Ingestion</h2>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                  Luna Multimodal Live Vision
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Snap or upload stock sheets, supplier delivery notes, or bills with automatic duplicate prevention
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
          {errorMsg && (
            <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-medium animate-shake">
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* View 1: Scanning / Preview Input */}
          {!scanResult && (
            <div className="space-y-5">
              {/* Camera Live Stream */}
              {cameraActive && (
                <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10 aspect-video flex items-center justify-center">
                  <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
                  <div className="absolute inset-0 border-2 border-cyan-400/40 pointer-events-none rounded-2xl m-4 flex flex-col justify-between p-3">
                    <div className="flex justify-between items-center text-[10px] font-mono text-cyan-400 bg-black/60 px-2 py-1 rounded backdrop-blur">
                      <span>ALIGN INVENTORY SHEET / BILL</span>
                      <span>1080P HD VISION</span>
                    </div>
                    <div className="text-center text-xs text-cyan-300 bg-black/60 px-3 py-1 rounded self-center backdrop-blur">
                      Hold still for sharp text extraction
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
                    <button
                      onClick={capturePhoto}
                      className="px-6 py-2.5 rounded-2xl bg-cyan-500 hover:bg-cyan-400 text-black font-black text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-cyan-500/30 active:scale-95 transition-all"
                    >
                      <Camera className="w-4 h-4" />
                      Capture Photo
                    </button>
                    <button
                      onClick={stopCamera}
                      className="px-4 py-2.5 rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Upload or Captured Preview */}
              {!cameraActive && previewUrl && (
                <div className="relative rounded-2xl overflow-hidden bg-black/40 border border-white/10 max-h-72 flex items-center justify-center group">
                  <img
                    src={previewUrl}
                    alt="Scanned inventory sheet"
                    className="max-h-72 w-full object-contain rounded-2xl"
                  />
                  <button
                    onClick={() => {
                      setPreviewUrl(null);
                      setSelectedFile(null);
                    }}
                    className="absolute top-3 right-3 p-2 rounded-xl bg-black/60 text-white hover:bg-black/80 backdrop-blur border border-white/10 transition-colors"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Input Choice Grid */}
              {!cameraActive && !previewUrl && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={startCamera}
                    className="p-8 rounded-2xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-cyan-500/40 flex flex-col items-center justify-center gap-3 text-center transition-all group cursor-pointer"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Camera className="w-7 h-7 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">Capture with Camera</p>
                      <p className="text-xs text-slate-400 mt-0.5">Take photo of stock sheet, bill or price board</p>
                    </div>
                  </button>

                  <label
                    className="p-8 rounded-2xl bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-emerald-500/40 flex flex-col items-center justify-center gap-3 text-center transition-all group cursor-pointer"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Upload className="w-7 h-7 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">Upload Image or PDF</p>
                      <p className="text-xs text-slate-400 mt-0.5">Select image file from device storage</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Optional Notes */}
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase font-mono tracking-wider text-slate-400">
                  Supplier / Batch Notes (Optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="e.g. Maliban Wholesale Bill #4920, Ceylon Biscuits delivery"
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder:text-slate-600 outline-none focus:border-cyan-500/40 font-mono"
                />
              </div>

              {/* Action Button */}
              {previewUrl && (
                <button
                  onClick={executeScan}
                  disabled={isScanning}
                  className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-black font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 active:scale-[0.99] transition-all disabled:opacity-50"
                >
                  {isScanning ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Ingesting via Luna Live Vision...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Scan & Auto-Add / Update Inventory
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* View 2: Scan Results & Deduplication Breakdown */}
          {scanResult && (
            <div className="space-y-5">
              {/* Summary KPIs */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3.5 rounded-2xl bg-white/[0.04] border border-white/10 text-center">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">Total Scanned</p>
                  <p className="text-xl font-black text-white mt-0.5">{scanResult.totalExtracted}</p>
                </div>
                <div className="p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-center">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-emerald-400">✨ New Products</p>
                  <p className="text-xl font-black text-emerald-300 mt-0.5">{scanResult.addedCount}</p>
                </div>
                <div className="p-3.5 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-center">
                  <p className="text-[10px] font-mono uppercase tracking-wider text-amber-400">🔄 Updated / Restocked</p>
                  <p className="text-xl font-black text-amber-300 mt-0.5">{scanResult.restockedCount}</p>
                </div>
              </div>

              {/* Deduplication Guarantee Note */}
              <div className="flex items-center gap-2.5 p-3 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs">
                <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
                <span>
                  <strong>Zero-Duplicate Sync:</strong> Existing products updated with new quantities, updated prices, and expiry dates. New products added cleanly!
                </span>
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 border-b border-white/10 pb-2">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                    activeTab === 'all'
                      ? 'bg-white/10 text-white font-bold'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  All Items ({scanResult.items.length})
                </button>
                <button
                  onClick={() => setActiveTab('created')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                    activeTab === 'created'
                      ? 'bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/30'
                      : 'text-slate-400 hover:text-emerald-300'
                  }`}
                >
                  ✨ New ({scanResult.addedCount})
                </button>
                <button
                  onClick={() => setActiveTab('restocked')}
                  className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all ${
                    activeTab === 'restocked'
                      ? 'bg-amber-500/20 text-amber-300 font-bold border border-amber-500/30'
                      : 'text-slate-400 hover:text-amber-300'
                  }`}
                >
                  🔄 Updated ({scanResult.restockedCount})
                </button>
              </div>

              {/* Itemized Table */}
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1 custom-scrollbar">
                {filteredItems.map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-3.5 rounded-2xl border flex items-center justify-between gap-3 ${
                      item.status === 'created'
                        ? 'bg-emerald-500/[0.04] border-emerald-500/20'
                        : 'bg-amber-500/[0.04] border-amber-500/20'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-xs text-white truncate">{item.name}</span>
                        {item.status === 'created' ? (
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold font-mono bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            NEW
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[9px] font-bold font-mono bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            UPDATED
                          </span>
                        )}
                        <span className="text-[10px] text-slate-500">{item.category}</span>
                        {item.priceUpdated && (
                          <span className="px-1.5 py-0.5 rounded text-[8px] font-bold font-mono bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                            PRICE UPDATED
                          </span>
                        )}
                        {item.expiryDate && (
                          <span className="px-1.5 py-0.5 rounded text-[8px] font-bold font-mono bg-purple-500/20 text-purple-300 border border-purple-500/30 flex items-center gap-1">
                            📅 Exp: {item.expiryDate}
                          </span>
                        )}
                        {item.batchNumber && (
                          <span className="px-1.5 py-0.5 rounded text-[8px] font-bold font-mono bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                            Lot: {item.batchNumber}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-400 mt-1 flex-wrap">
                        <span>
                          Selling:{' '}
                          {item.priceUpdated && item.previousPrice ? (
                            <span>
                              <span className="line-through text-slate-500 mr-1">Rs. {item.previousPrice}</span>
                              <strong className="text-cyan-300">Rs. {item.sellingPrice}</strong>
                            </span>
                          ) : (
                            <strong className="text-white">Rs. {item.sellingPrice}</strong>
                          )}
                        </span>
                        {item.costPrice && <span>Cost: <strong>Rs. {item.costPrice}</strong></span>}
                        {item.barcode && <span className="font-mono text-[10px] text-cyan-400/80">BC: {item.barcode}</span>}
                      </div>
                    </div>

                    {/* Stock Column */}
                    <div className="text-right shrink-0">
                      {item.status === 'created' ? (
                        <div>
                          <span className="text-xs font-mono font-bold text-emerald-400">+{item.restockQty}</span>
                          <span className="text-[10px] text-slate-500 ml-1">{item.unit}</span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-[10px] text-slate-500 line-through mr-1">{item.previousStock}</span>
                          <span className="text-xs font-mono font-bold text-amber-300">➔ {item.newStock}</span>
                          <span className="text-[10px] text-slate-500 ml-1">{item.unit}</span>
                          <p className="text-[9px] text-emerald-400">+{item.restockQty} added</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setScanResult(null);
                    setPreviewUrl(null);
                    setSelectedFile(null);
                  }}
                  className="flex-1 py-3 rounded-2xl bg-white/10 hover:bg-white/15 text-white font-bold text-xs uppercase tracking-wider transition-all"
                >
                  Scan Another Sheet
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 rounded-2xl bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs uppercase tracking-wider transition-all shadow-lg shadow-emerald-500/20"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
